import React, { useCallback, useMemo } from "react";
import { Disclosure } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { useNuqsParams, intParam, stringParam, arrayParam } from "@/hooks/useNuqsParams";
import { requestCloseSlideOver } from "@/redux/actions/slide_over";
import { defaultEmployeesFilters } from "@/redux/reducers/employees";
import { Button } from "@/components/button/button";

export const EMPLOYEE_ROLES = {
  OWN: {
    ID: "OWN",
    NAME: "Owner",
    DESCRIPTION: "Full access to all features and data.",
  },
  ADM: {
    ID: "ADM",
    NAME: "Broker",
    DESCRIPTION: "Add team members, manage permissions, and access all data.",
  },
  BRO: {
    ID: "BRO",
    NAME: "Processor",
    DESCRIPTION:
      "View and manage deals that you assign to them and access contacts. Cannot add/update team members or roles.",
  },
  AFF: {
    ID: "AFF",
    NAME: "Affiliate",
    DESCRIPTION: "View and manage deals that you assign to them. Cannot access/view contacts, members, or roles.",
  },
};

function EmployeesCategoryFilter() {
  const dispatch = useDispatch();

  const [query, setQuery] = useNuqsParams({
    page: intParam(defaultEmployeesFilters.page),
    size: intParam(defaultEmployeesFilters.size),
    sortBy: intParam(defaultEmployeesFilters.sortBy),
    searchTerm: stringParam(defaultEmployeesFilters.searchTerm),
    roles: arrayParam(defaultEmployeesFilters.roles),
  });

  const roleFiltersApplied = useMemo(() => query.roles.length, [query]);

  const checkRole = value => {
    setQuery(currentQuery => {
      if (!currentQuery.roles.includes(value)) {
        return {
          ...currentQuery,
          page: 1,
          roles: currentQuery.roles.concat(value),
        };
      }

      return {
        ...currentQuery,
        page: 1,
        roles: currentQuery.roles.filter(filter => filter !== value),
      };
    });
  };

  const handleClear = useCallback(() => {
    setQuery(currentQuery => ({
      ...currentQuery,
      ...defaultEmployeesFilters,
    }));
    dispatch(requestCloseSlideOver());
  }, [dispatch, setQuery]);

  return (
    <div className="divide-y divide-gray-200 dark:divide-slate-600">
      <Disclosure
        defaultOpen
        as="div"
        key=""
        className="h-full border-t border-gray-200 px-4 py-6 dark:border-slate-400"
      >
        {({ open }) => (
          <>
            <h3 className="-mx-2 -my-3 flow-root">
              <Disclosure.Button className="flex w-full items-center justify-between px-2 py-1 text-gray-700 hover:text-gray-500 dark:text-slate-100 dark:hover:text-gray-400">
                <span className="text-sm font-semibold">Member Roles</span>
                <span className="ml-6 flex items-center">
                  {open ? (
                    <MinusIcon className="size-5" aria-hidden="true" />
                  ) : (
                    <PlusIcon className="size-5" aria-hidden="true" />
                  )}
                </span>
              </Disclosure.Button>
            </h3>
            <Disclosure.Panel className="pt-6">
              <div className="space-y-6">
                {Object.keys(EMPLOYEE_ROLES).map(role => (
                  <div key={`container_id_${role}`} className="flex items-center">
                    <input
                      id={`input_id_${role}`}
                      name={`input_name_${role}`}
                      defaultValue={query.roles.includes(role)}
                      type="checkbox"
                      onChange={() => checkRole(role)}
                      defaultChecked={query.roles.includes(role)}
                      className={classNames(
                        "h-4 w-4 border-gray-300 dark:border-none rounded text-indigo-600 focus:ring-indigo-500",
                        { "dark:bg-slate-300": !query.roles.includes(role.id) },
                      )}
                    />
                    <label
                      htmlFor={`label_name_${role}`}
                      className="ml-3 min-w-0 flex-1 text-sm text-gray-500 dark:text-slate-400"
                    >
                      {EMPLOYEE_ROLES[role].NAME}
                    </label>
                  </div>
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      <div className="sticky bottom-0 flex flex-none justify-end space-x-2 bg-white py-5 pr-5 dark:bg-slate-800">
        <Button label="Clear All" type="button" onClick={handleClear} isDisabled={!roleFiltersApplied} />
      </div>
    </div>
  );
}

export default EmployeesCategoryFilter;
